import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';

const KEY = 'lm_cookie_consent_v1';

export default function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setShow(true);
    } catch {
      // localStorage blocked — don't show banner; banner is informational only
    }
  }, []);

  const accept = () => {
    try { localStorage.setItem(KEY, JSON.stringify({ accepted: true, ts: Date.now() })); } catch {}
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-0 bottom-0 z-[1000] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="max-w-3xl mx-auto pointer-events-auto rounded-2xl border border-white/10 bg-[#0A0B0F]/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center">
                <Cookie className="w-4 h-4 text-white/70" />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] text-white/85 font-medium leading-snug">
                  We only use cookies that keep you logged in.
                </p>
                <p className="text-[12px] text-white/55 mt-1 leading-relaxed">
                  No ads, no tracking pixels, no third‑party analytics. Read our{' '}
                  <Link to="/privacy" className="underline underline-offset-2 hover:text-white text-white/75" data-testid="cookie-banner-privacy-link">
                    Privacy Policy
                  </Link>{' '}
                  for the details.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 self-stretch sm:self-auto">
              <button
                onClick={accept}
                className="px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors"
                data-testid="cookie-banner-accept"
              >
                Got it
              </button>
              <button
                onClick={accept}
                aria-label="Dismiss"
                className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 flex items-center justify-center transition-colors"
                data-testid="cookie-banner-dismiss"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
